import { erase } from "browser-cookies";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getCookie } from "@vfuk/core-helpers";
import Spacing from "@vfuk/core-spacing";

import { IconScreen, SpinnerLoader, getHostName } from "@ucchub/ui";
import { LOCAL_STORAGE_USER_SESSION_ID_KEY } from "@ucchub/ui/src/utils/createBaseService";

import DashShell from "@components/common/DashShell";
import AuthenticationService from "@services/AuthenticationService";

const deleteAllLegacyAuthCookies = () => {
  // delete all Legacy Auth Cookies
  // this function can be removed after releasing this code on prod
  erase("Token");
  erase("refreshToken");
  erase("Header");
  sessionStorage.removeItem(LOCAL_STORAGE_USER_SESSION_ID_KEY);
};

const deleteAllCookies = () => {
  // Delete all cookies
  deleteAllLegacyAuthCookies();

  erase("Token", { domain: getHostName() });
  erase("refreshToken", { domain: getHostName() });
  erase("Header", { domain: getHostName() });
  sessionStorage.removeItem(LOCAL_STORAGE_USER_SESSION_ID_KEY);
};

const Logout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation();

  const token = getCookie("Token");

  // Sends request to the logout API and redirects to the home page
  const logOutUser = useCallback(async () => {
    setError("");
    setIsLoading(true);

    const res = await AuthenticationService.logout();

    // After the request has been made, we set loading to false.
    setIsLoading(false);

    // If the request fails, we'll display a message & a retry button.
    if (res.status !== 200) {
      return setError(
        `There was an error logging out: ${res.message} (${res.response.data.message})`
      );
    }

    // If the request succeeds, clear the cookies.
    deleteAllCookies();
    sessionStorage.clear();
    localStorage.clear();
    // We can't use `useNavigate`, because we need a full page refresh to refresh the cookies.
    // client-side navigation doesn't update the cookies.
    window.location.href = "/";
  }, []);

  useEffect(() => {
    // If the user is not logged in, redirect back to the homepage.
    if (!token) {
      window.location.href = "/";
      return;
    }
    logOutUser();
  }, [logOutUser, token]);

  return (
    <DashShell hideCrumbs>
      {isLoading && (
        <Spacing spacingLevel={{ top: 6, bottom: 6 }}>
          <SpinnerLoader
            srText={t("core:auth:loading_logout") || "Logging out..."}
            text={t("core:auth:loading_logout") || "Logging out..."}
          />
        </Spacing>
      )}

      {error && (
        <IconScreen
          title="There was an error logging you out."
          description={error}
          buttonOnClick={() => logOutUser()}
          buttonText="Try again"
        />
      )}
    </DashShell>
  );
};

export default Logout;
